import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import QRCodeCard from "@/components/PlayerQRCode";
import ImageInput from "@/components/ImageInput";
import VideoInput from "@/components/VideoInput";
import AppearancePanel from "@/components/AppearancePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { AssociationsAPI, CompaniesAPI, formatApiError } from "@/lib/api";
import { ArrowLeft, Plus, Trash2, Save, ChevronUp, ChevronDown } from "lucide-react";

const COMMITTEE_ROLES = ["Président", "Vice-président", "Secrétaire", "Trésorier", "Membre du comité"];

const inputClass =
  "bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md";

const SectionTitle = ({ children }) => (
  <h2 className="font-display text-xl font-bold uppercase tracking-tight text-[#D4AF37] border-b border-white/5 pb-2 mt-2">
    {children}
  </h2>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</Label>
    {children}
  </div>
);

const emptyOrg = {
  name: "",
  founded_year: "",
  subtitle: "",
  history: "",
  activity: "",
  logo_url: "",
  cover_image_url: "",
  primary_color: "#D4AF37",
  milestones: [],
  achievements: [],
  gallery: [],
  highlights: [],
  appearance: {},
  committee_enabled: false,
  committee: [],
  employees_enabled: false,
  employees: [],
  standings_enabled: false,
  standings_title: "Challenge interne",
  internal_standings: [],
};

export default function AdminOrganization({ type = "association" }) {
  const isCompany = type === "company";
  const apiRef = isCompany ? CompaniesAPI : AssociationsAPI;
  const base = isCompany ? "/admin/companies" : "/admin/associations";
  const publicPath = isCompany ? "/entreprise" : "/association";
  const { id } = useParams();
  const isEdit = id && id !== "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyOrg);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    apiRef
      .get(id)
      .then((d) => setForm({ ...emptyOrg, ...d }))
      .catch(() => toast.error("Profil introuvable"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addItem = (k, item) => setForm((f) => ({ ...f, [k]: [...(f[k] || []), item] }));
  const updateItem = (k, i, patch) =>
    setForm((f) => ({ ...f, [k]: f[k].map((x, idx) => (idx === i ? { ...x, ...patch } : x)) }));
  const removeItem = (k, i) =>
    setForm((f) => ({ ...f, [k]: f[k].filter((_, idx) => idx !== i) }));
  const moveItem = (k, i, dir) =>
    setForm((f) => {
      const arr = [...f[k]];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...f, [k]: arr };
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Le nom est obligatoire");
    setSaving(true);
    try {
      if (isEdit) {
        await apiRef.update(id, form);
        toast.success("Profil enregistré");
      } else {
        const created = await apiRef.create(form);
        toast.success("Profil créé");
        navigate(`${base}/${created.id}`, { replace: true });
      }
    } catch (err) {
      toast.error(formatApiError(err, "Enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-500 font-body flex items-center justify-center">
        Chargement...
      </div>
    );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
        <Link to={base} className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-xs uppercase tracking-[0.18em] font-bold mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> {isCompany ? "Entreprises" : "Associations"}
        </Link>
        <h1 className="font-display text-3xl font-black uppercase tracking-tight mb-8">
          {isEdit ? `Éditer — ${form.name}` : isCompany ? "Nouvelle entreprise" : "Nouvelle association"}
        </h1>

        <form onSubmit={submit} className="space-y-8">
          <SectionTitle>Identité</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nom">
              <Input className={inputClass} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="org-name-input" placeholder={isCompany ? "Mon Entreprise SA" : "ACGF"} />
            </Field>
            <Field label="Année de création">
              <Input className={inputClass} value={form.founded_year} onChange={(e) => set("founded_year", e.target.value)} placeholder="1987" data-testid="org-founded-input" />
            </Field>
            <Field label="Sous-titre (optionnel)">
              <Input className={inputClass} value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder={isCompany ? "Votre partenaire de confiance" : "Association Cantonale Genevoise de Football"} />
            </Field>
            <Field label="Couleur principale">
              <div className="flex gap-2 items-center">
                <input type="color" value={form.primary_color || "#D4AF37"} onChange={(e) => set("primary_color", e.target.value)} className="w-10 h-10 rounded cursor-pointer bg-transparent border border-white/10" />
                <Input className={inputClass} value={form.primary_color} onChange={(e) => set("primary_color", e.target.value)} />
              </div>
            </Field>
            <Field label="Logo">
              <ImageInput value={form.logo_url} onChange={(v) => set("logo_url", v)} testId="org-logo-input" size="md" placeholder="https://..." />
            </Field>
            <Field label="Photo de couverture">
              <ImageInput value={form.cover_image_url} onChange={(v) => set("cover_image_url", v)} testId="org-cover-input" aspect="wide" placeholder="https://..." />
            </Field>
          </div>

          {isCompany && (
            <Field label="Activité">
              <Textarea className={`${inputClass} min-h-[70px]`} value={form.activity} onChange={(e) => set("activity", e.target.value)} placeholder="Notre cœur de métier..." data-testid="org-activity-input" />
            </Field>
          )}
          <Field label={isCompany ? "Présentation / Histoire" : "Histoire de l'association"}>
            <Textarea className={`${inputClass} min-h-[120px]`} value={form.history} onChange={(e) => set("history", e.target.value)} placeholder="Raconte l'histoire..." data-testid="org-history-input" />
          </Field>

          <SectionTitle>Design & Couleurs</SectionTitle>
          <AppearancePanel
            appearance={form.appearance || {}}
            onChange={(v) => set("appearance", v)}
            imageUrl={form.logo_url || form.cover_image_url}
            accent={form.primary_color}
            onAccent={(v) => set("primary_color", v)}
            testIdPrefix="org-ap"
          />

          <SectionTitle>Moments importants</SectionTitle>
          <div className="space-y-3">
            {(form.milestones || []).map((m, i) => (
              <div key={m.id || i} className="grid sm:grid-cols-[100px,1fr,auto] gap-2 items-start bg-[#0A0B0E] border border-white/5 rounded-md p-3">
                <Input className={inputClass} placeholder="1995" value={m.date || ""} onChange={(e) => updateItem("milestones", i, { date: e.target.value })} />
                <div className="space-y-2">
                  <Input className={inputClass} placeholder="Titre du moment" value={m.title || ""} onChange={(e) => updateItem("milestones", i, { title: e.target.value })} />
                  <Input className={inputClass} placeholder="Description (optionnel)" value={m.description || ""} onChange={(e) => updateItem("milestones", i, { description: e.target.value })} />
                </div>
                <Button type="button" variant="ghost" className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10" onClick={() => removeItem("milestones", i)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" data-testid="org-add-milestone" onClick={() => addItem("milestones", { date: "", title: "", description: "", image_url: "" })} className="bg-transparent border-white/15 hover:bg-white/5 text-white">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un moment
            </Button>
          </div>

          <SectionTitle>Réalisations</SectionTitle>
          <div className="space-y-3">
            {(form.achievements || []).map((a, i) => (
              <div key={a.id || i} className="grid sm:grid-cols-[100px,1fr,auto] gap-2 items-start bg-[#0A0B0E] border border-white/5 rounded-md p-3">
                <Input className={inputClass} placeholder="2024" value={a.date || ""} onChange={(e) => updateItem("achievements", i, { date: e.target.value })} />
                <div className="space-y-2">
                  <Input className={inputClass} placeholder="Titre de la réalisation" value={a.title || ""} onChange={(e) => updateItem("achievements", i, { title: e.target.value })} />
                  <Input className={inputClass} placeholder="Description (optionnel)" value={a.description || ""} onChange={(e) => updateItem("achievements", i, { description: e.target.value })} />
                </div>
                <Button type="button" variant="ghost" className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10" onClick={() => removeItem("achievements", i)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" data-testid="org-add-achievement" onClick={() => addItem("achievements", { date: "", title: "", description: "", image_url: "" })} className="bg-transparent border-white/15 hover:bg-white/5 text-white">
              <Plus className="w-4 h-4 mr-2" /> Ajouter une réalisation
            </Button>
          </div>

          {!isCompany && (
            <>
              <SectionTitle>Comité et membres</SectionTitle>
              <div className="flex items-center gap-3">
                <Switch checked={!!form.committee_enabled} onCheckedChange={(v) => set("committee_enabled", v)} data-testid="org-committee-toggle" />
                <span className="font-body text-sm text-zinc-400">
                  {form.committee_enabled ? "Affiché sur la page publique" : "Désactivé (rien ne s'affiche publiquement)"}
                </span>
              </div>
              {form.committee_enabled && (
                <div className="space-y-3">
                  {(form.committee || []).map((m, i) => {
                    const isCustomRole = m.role !== "" && !COMMITTEE_ROLES.includes(m.role);
                    return (
                      <div key={m.id || i} className="bg-[#0A0B0E] border border-white/5 rounded-md p-3 space-y-2" data-testid={`org-committee-member-${i}`}>
                        <div className="grid sm:grid-cols-2 gap-2">
                          <Input className={inputClass} placeholder="Prénom" value={m.first_name || ""} onChange={(ev) => updateItem("committee", i, { first_name: ev.target.value })} data-testid={`org-committee-firstname-${i}`} />
                          <Input className={inputClass} placeholder="Nom" value={m.last_name || ""} onChange={(ev) => updateItem("committee", i, { last_name: ev.target.value })} data-testid={`org-committee-lastname-${i}`} />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2">
                          <select
                            className="h-10 w-full rounded-md bg-[#0E0F13] border border-white/10 text-white text-sm px-3 focus:border-[#D4AF37] focus:outline-none"
                            value={isCustomRole ? "__custom__" : m.role || ""}
                            onChange={(ev) => updateItem("committee", i, { role: ev.target.value === "__custom__" ? " " : ev.target.value })}
                            data-testid={`org-committee-role-${i}`}
                          >
                            <option value="">Fonction...</option>
                            {COMMITTEE_ROLES.map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                            <option value="__custom__">Autre fonction...</option>
                          </select>
                          {isCustomRole && (
                            <Input className={inputClass} placeholder="Fonction personnalisée" value={m.role.trim() === "" ? "" : m.role} onChange={(ev) => updateItem("committee", i, { role: ev.target.value || " " })} data-testid={`org-committee-customrole-${i}`} />
                          )}
                        </div>
                        <Textarea className={`${inputClass} min-h-[60px]`} placeholder="Courte présentation (facultatif)" value={m.description || ""} onChange={(ev) => updateItem("committee", i, { description: ev.target.value })} />
                        <div className="flex items-center gap-2 flex-wrap">
                          <ImageInput value={m.photo_url} onChange={(v) => updateItem("committee", i, { photo_url: v })} testId={`org-committee-photo-${i}`} size="sm" placeholder="Photo (facultatif)" />
                          <div className="ml-auto flex items-center gap-1">
                            <Button type="button" variant="ghost" disabled={i === 0} className="text-zinc-400 hover:text-white hover:bg-white/5 h-8 w-8 p-0" onClick={() => moveItem("committee", i, -1)} data-testid={`org-committee-up-${i}`}>
                              <ChevronUp className="w-4 h-4" />
                            </Button>
                            <Button type="button" variant="ghost" disabled={i === (form.committee || []).length - 1} className="text-zinc-400 hover:text-white hover:bg-white/5 h-8 w-8 p-0" onClick={() => moveItem("committee", i, 1)} data-testid={`org-committee-down-${i}`}>
                              <ChevronDown className="w-4 h-4" />
                            </Button>
                            <Button type="button" variant="ghost" className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" onClick={() => removeItem("committee", i)} data-testid={`org-committee-delete-${i}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <Button type="button" variant="outline" data-testid="org-add-committee-member" onClick={() => addItem("committee", { first_name: "", last_name: "", role: "", photo_url: "", description: "" })} className="bg-transparent border-white/15 hover:bg-white/5 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter un membre
                  </Button>
                </div>
              )}
            </>
          )}

          {isCompany && (
            <>
              <SectionTitle>Option A — Profils des employés</SectionTitle>
              <div className="flex items-center gap-3">
                <Switch checked={!!form.employees_enabled} onCheckedChange={(v) => set("employees_enabled", v)} data-testid="org-employees-toggle" />
                <span className="font-body text-sm text-zinc-400">
                  {form.employees_enabled ? "Affichés sur la page publique" : "Désactivé (rien ne s'affiche publiquement)"}
                </span>
              </div>
              {form.employees_enabled && (
                <div className="space-y-3">
                  {(form.employees || []).map((e, i) => (
                    <div key={e.id || i} className="bg-[#0A0B0E] border border-white/5 rounded-md p-3 space-y-2">
                      <div className="grid sm:grid-cols-2 gap-2">
                        <Input className={inputClass} placeholder="Nom" value={e.name || ""} onChange={(ev) => updateItem("employees", i, { name: ev.target.value })} data-testid={`org-employee-name-${i}`} />
                        <Input className={inputClass} placeholder="Fonction" value={e.role || ""} onChange={(ev) => updateItem("employees", i, { role: ev.target.value })} />
                      </div>
                      <Input className={inputClass} placeholder="Réalisations / description" value={e.description || ""} onChange={(ev) => updateItem("employees", i, { description: ev.target.value })} />
                      <div className="flex items-center gap-3 flex-wrap">
                        <ImageInput value={e.photo_url} onChange={(v) => updateItem("employees", i, { photo_url: v })} testId={`org-employee-photo-${i}`} size="sm" placeholder="Photo (URL)" />
                        <label className="flex items-center gap-2 text-xs text-zinc-400 font-body">
                          <Switch checked={e.visible !== false} onCheckedChange={(v) => updateItem("employees", i, { visible: v })} />
                          Visible publiquement
                        </label>
                        <Button type="button" variant="ghost" className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 ml-auto" onClick={() => removeItem("employees", i)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button type="button" variant="outline" data-testid="org-add-employee" onClick={() => addItem("employees", { name: "", role: "", photo_url: "", description: "", visible: true })} className="bg-transparent border-white/15 hover:bg-white/5 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter un employé
                  </Button>
                </div>
              )}

              <SectionTitle>Option B — Classement / Challenge interne</SectionTitle>
              <div className="flex items-center gap-3">
                <Switch checked={!!form.standings_enabled} onCheckedChange={(v) => set("standings_enabled", v)} data-testid="org-standings-toggle" />
                <span className="font-body text-sm text-zinc-400">
                  {form.standings_enabled ? "Affiché sur la page publique" : "Désactivé (rien ne s'affiche publiquement)"}
                </span>
              </div>
              {form.standings_enabled && (
                <div className="space-y-3">
                  <Field label="Titre du challenge">
                    <Input className={inputClass} value={form.standings_title || ""} onChange={(e) => set("standings_title", e.target.value)} placeholder="Challenge commercial 2026" data-testid="org-standings-title" />
                  </Field>
                  {(form.internal_standings || []).map((r, i) => (
                    <div key={r.id || i} className="grid grid-cols-[1fr,1fr,90px,auto] gap-2 items-center bg-[#0A0B0E] border border-white/5 rounded-md p-3">
                      <Input className={inputClass} placeholder="Participant" value={r.name || ""} onChange={(e) => updateItem("internal_standings", i, { name: e.target.value })} data-testid={`org-standing-name-${i}`} />
                      <Input className={inputClass} placeholder="Équipe (optionnel)" value={r.team || ""} onChange={(e) => updateItem("internal_standings", i, { team: e.target.value })} />
                      <Input className={inputClass} type="number" placeholder="PTS" value={r.points ?? 0} onChange={(e) => updateItem("internal_standings", i, { points: Number(e.target.value) || 0 })} />
                      <Button type="button" variant="ghost" className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10" onClick={() => removeItem("internal_standings", i)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" data-testid="org-add-standing" onClick={() => addItem("internal_standings", { name: "", team: "", points: 0 })} className="bg-transparent border-white/15 hover:bg-white/5 text-white">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter un participant
                  </Button>
                </div>
              )}
            </>
          )}

          <SectionTitle>Galerie photos</SectionTitle>
          <div className="space-y-3">
            {(form.gallery || []).map((g, i) => (
              <div key={g.id || i} className="flex gap-2 items-center bg-[#0A0B0E] border border-white/5 rounded-md p-3 flex-wrap">
                <ImageInput value={g.url} onChange={(v) => updateItem("gallery", i, { url: v })} testId={`org-gallery-${i}`} size="sm" placeholder="URL de l'image" />
                <Input className={`${inputClass} flex-1 min-w-[140px]`} placeholder="Légende (optionnel)" value={g.caption || ""} onChange={(e) => updateItem("gallery", i, { caption: e.target.value })} />
                <Button type="button" variant="ghost" className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10" onClick={() => removeItem("gallery", i)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" data-testid="org-add-photo" onClick={() => addItem("gallery", { url: "", caption: "" })} className="bg-transparent border-white/15 hover:bg-white/5 text-white">
              <Plus className="w-4 h-4 mr-2" /> Ajouter une photo
            </Button>
          </div>

          <SectionTitle>Vidéos</SectionTitle>
          <div className="space-y-3">
            {(form.highlights || []).map((h, i) => (
              <div key={h.id || i} className="bg-[#0A0B0E] border border-white/5 rounded-md p-3 space-y-2">
                <Input className={inputClass} placeholder="Titre de la vidéo" value={h.title || ""} onChange={(e) => updateItem("highlights", i, { title: e.target.value })} />
                <div className="flex gap-2 items-center flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <VideoInput value={h.url || ""} onChange={(url) => updateItem("highlights", i, { url })} testId={`org-video-${i}`} />
                  </div>
                  <Button type="button" variant="ghost" className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10" onClick={() => removeItem("highlights", i)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" data-testid="org-add-video" onClick={() => addItem("highlights", { title: "", type: "youtube", url: "" })} className="bg-transparent border-white/15 hover:bg-white/5 text-white">
              <Plus className="w-4 h-4 mr-2" /> Ajouter une vidéo
            </Button>
          </div>

          {isEdit && form.slug && (
            <>
              <SectionTitle>QR Code (gravure)</SectionTitle>
              <QRCodeCard
                url={`${window.location.origin}${publicPath}/${form.slug}`}
                title={form.name}
                subtitle={isCompany ? "Entreprise connectée" : "Association connectée"}
                fileBase={`${type}-${form.slug}`}
                primaryColor={form.primary_color || "#D4AF37"}
              />
            </>
          )}

          <div className="sticky bottom-4 z-10 flex gap-3">
            <Button type="submit" disabled={saving} data-testid="org-save-btn" className="flex-1 h-12 bg-[#D4AF37] text-black hover:bg-[#c9a430] font-bold uppercase tracking-[0.18em]">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
            {isEdit && form.slug && (
              <Button type="button" variant="outline" onClick={() => window.open(`${publicPath}/${form.slug}`, "_blank")} className="h-12 bg-transparent border-white/15 hover:bg-white/5 text-white">
                Voir la page
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
